// services/teamService.js
// Team Hub Service — Team members, announcements, internal messages
// Place this file in: src/services/teamService.js

import { db } from '../firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  collection, addDoc, getDocs, updateDoc, deleteDoc, doc, 
  query, where, serverTimestamp 
} from 'firebase/firestore';

// ============================================================================
// TEAM MEMBERS
// ============================================================================

// `creatorUid` is whoever is SUBMITTING this (the admin/founder using the
// Add Team Member or Send Invitation form) — it is recorded as
// firmOwnerId, the legacy "who manages this roster" concept, but must
// NEVER be written into `userId`. Before the invitee has registered, no
// real uid exists yet for them, so `userId` stays null/absent rather than
// being misattributed to the creator (duplicate-membership forensic
// report, 2026-09-26: teamMembers.userId pointing at the ADMIN's own uid
// on a not-yet-accepted placeholder). `memberData.userId` is only ever
// non-null when a caller explicitly already knows the real subject's uid.
// Seat limits (2026-10-15): roster seats are created server-side (addRosterMember),
// which enforces the plan's user limit; the caller becomes firmOwnerId, userId stays null.
export const addTeamMember = async (creatorUid, memberData, firmId) => {
  try {
    const r = await httpsCallable(getFunctions(), 'addRosterMember')({ firmId, member: { ...memberData, email: (memberData.email || '').toLowerCase().trim() } });
    return { success: true, id: r.data.id };
  } catch (e) {
    return { success: false, error: (e && e.details && e.details.reason === 'SEAT_LIMIT_REACHED') ? "Your plan's user limit has been reached. Upgrade the plan, or remove a member or a pending invitation first." : (e && e.message) || 'Could not complete this request.', seatLimit: !!(e && e.details && e.details.reason === 'SEAT_LIMIT_REACHED') };
  }
};

export const getTeamMembers = async (userId, firmId) => {
  try {
    let members = [];

    // Get by firmId
    if (firmId) {
      const q = query(collection(db, 'teamMembers'), where('firmId', '==', firmId));
      const snapshot = await getDocs(q);
      members = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // No firmOwnerId (creator-field) fallback: team data is read ONLY through the
    // current firm's tenant-scoped query above (a firm-constrained variant would
    // be a strict subset of it; an owner-only query would not survive gating).

    // Deduplicate
    members = members.filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i);
    // Archived duplicate-membership records (duplicate-membership
    // remediation, 2026-09-26) are retained for audit only — never shown
    // in Team Management. Seats retired by removeFirmMember (status
    // 'removed') are likewise kept as history, not shown as current members.
    members = members.filter(m => m.status !== 'archived' && m.status !== 'removed');
    members.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { success: true, members };
  } catch (error) {
    return { success: false, error: error.message, members: [] };
  }
};

export const updateTeamMember = async (memberId, updates) => {
  try {
    await updateDoc(doc(db, 'teamMembers', memberId), {
      ...updates,
      ...(updates.email ? { email: updates.email.toLowerCase().trim() } : {}),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteTeamMember = async (memberId) => {
  try {
    await deleteDoc(doc(db, 'teamMembers', memberId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// ANNOUNCEMENTS / INTERNAL MESSAGES
// ============================================================================

export const createAnnouncement = async (userId, announcementData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'teamAnnouncements'), {
      ...announcementData,
      userId: userId,
      firmOwnerId: userId,
      firmId: firmId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getAnnouncements = async (userId, firmId) => {
  try {
    let announcements = [];

    if (firmId) {
      const q = query(collection(db, 'teamAnnouncements'), where('firmId', '==', firmId));
      const snapshot = await getDocs(q);
      announcements = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // No firmOwnerId (creator-field) fallback: team data is read ONLY through the
    // current firm's tenant-scoped query above (a firm-constrained variant would
    // be a strict subset of it; an owner-only query would not survive gating).

    announcements = announcements.filter((a, i, arr) => arr.findIndex(x => x.id === a.id) === i);
    announcements.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    return { success: true, announcements };
  } catch (error) {
    return { success: false, error: error.message, announcements: [] };
  }
};

export const deleteAnnouncement = async (announcementId) => {
  try {
    await deleteDoc(doc(db, 'teamAnnouncements', announcementId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// TEAM TASK ASSIGNMENTS
// ============================================================================

export const assignTeamTask = async (userId, taskData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'teamTasks'), {
      ...taskData,
      userId: userId,
      firmOwnerId: userId,
      firmId: firmId,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getTeamTasks = async (userId, firmId) => {
  try {
    let tasks = [];

    if (firmId) {
      const q = query(collection(db, 'teamTasks'), where('firmId', '==', firmId));
      const snapshot = await getDocs(q);
      tasks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // No firmOwnerId (creator-field) fallback: team data is read ONLY through the
    // current firm's tenant-scoped query above (a firm-constrained variant would
    // be a strict subset of it; an owner-only query would not survive gating).

    tasks = tasks.filter((t, i, arr) => arr.findIndex(x => x.id === t.id) === i);
    tasks.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    return { success: true, tasks };
  } catch (error) {
    return { success: false, error: error.message, tasks: [] };
  }
};

export const updateTeamTask = async (taskId, updates) => {
  try {
    await updateDoc(doc(db, 'teamTasks', taskId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteTeamTask = async (taskId) => {
  try {
    await deleteDoc(doc(db, 'teamTasks', taskId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// ROLE DEFINITIONS
// ============================================================================

export const TEAM_ROLES = [
  { id: 'partner', label: 'Partner', color: '#FFD700' },
  { id: 'senior_associate', label: 'Senior Associate', color: '#667eea' },
  { id: 'associate', label: 'Associate', color: '#3b82f6' },
  { id: 'junior_associate', label: 'Junior Associate', color: '#06b6d4' },
  { id: 'paralegal', label: 'Paralegal', color: '#10b981' },
  { id: 'legal_assistant', label: 'Legal Assistant', color: '#8b5cf6' },
  { id: 'clerk', label: 'Clerk', color: '#f59e0b' },
  { id: 'intern', label: 'Intern', color: '#a8adb7' },
  { id: 'secretary', label: 'Secretary', color: '#ec4899' },
  { id: 'accountant', label: 'Accountant', color: '#059669' },
  { id: 'it', label: 'IT Support', color: '#d97706' },
  { id: 'admin', label: 'Admin', color: '#6b7280' },
  { id: 'agent', label: 'Agent', color: '#a855f7' },
];

export const DEPARTMENTS = ['Litigation', 'Corporate', 'Real Estate', 'Family Law', 'Criminal', 'IP', 'Tax', 'HR', 'Admin', 'Other'];

// ============================================================================
// TEAM CHAT — Channels & Messages
// ============================================================================

export const createChannel = async (userId, channelData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'teamChannels'), {
      ...channelData,
      userId: userId,
      firmOwnerId: userId,
      firmId: firmId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getChannels = async (userId, firmId) => {
  try {
    let channels = [];

    if (firmId) {
      const q = query(collection(db, 'teamChannels'), where('firmId', '==', firmId));
      const snapshot = await getDocs(q);
      channels = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // No firmOwnerId (creator-field) fallback: team data is read ONLY through the
    // current firm's tenant-scoped query above (a firm-constrained variant would
    // be a strict subset of it; an owner-only query would not survive gating).

    channels = channels.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
    channels.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { success: true, channels };
  } catch (error) {
    return { success: false, error: error.message, channels: [] };
  }
};

export const deleteChannel = async (channelId) => {
  try {
    await deleteDoc(doc(db, 'teamChannels', channelId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const sendTeamMessage = async (userId, messageData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'teamMessages'), {
      ...messageData,
      userId: userId,
      firmOwnerId: userId,
      firmId: firmId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getTeamMessages = async (userId, channelId, firmId) => {
  try {
    let messages = [];

    if (firmId) {
      const q = query(collection(db, 'teamMessages'), where('firmId', '==', firmId), where('channelId', '==', channelId));
      const snapshot = await getDocs(q);
      messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // No firmOwnerId (creator-field) fallback: team data is read ONLY through the
    // current firm's tenant-scoped query above (a firm-constrained variant would
    // be a strict subset of it; an owner-only query would not survive gating).

    messages = messages.filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i);
    messages.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return da - db2;
    });
    return { success: true, messages };
  } catch (error) {
    return { success: false, error: error.message, messages: [] };
  }
};

export const deleteTeamMessage = async (messageId) => {
  try {
    await deleteDoc(doc(db, 'teamMessages', messageId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Default channels to seed
export const DEFAULT_CHANNELS = [
  { name: 'General', description: 'General team discussion', icon: '💬' },
  { name: 'Case Discussion', description: 'Discuss active cases', icon: '⚖️' },
  { name: 'Announcements', description: 'Important firm announcements', icon: '📢' },
  { name: 'Research', description: 'Share legal research & resources', icon: '📚' },
  { name: 'Social', description: 'Off-topic & social chat', icon: '🎉' },
];
